// iPhone Safari can lose photos picked in a file input before the form is
// submitted; it then posts an empty body, which Rails rejects (404 on edit,
// 422 on create). Each picked file is therefore read into memory right away
// (large photos and HEIC are downscaled to JPEG) and the in-memory copies are
// put back into the input, so the normal form submit no longer depends on the
// phone's copy of the file. Inputs with data-manual-files handle this themselves.

const MAX_IMAGE_EDGE = 2000;
const HEIC_PATTERN = /\.hei[cf]$/i;

let pending = 0;
let queuedForm = null;
let pickCount = 0;

const loadImage = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
  img.src = url;
});

const toJpeg = (img) => new Promise((resolve, reject) => {
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/jpeg', 0.85);
});

const prepareFile = (file) => file.arrayBuffer().then((buffer) => {
  if (buffer.byteLength === 0) { throw new Error('empty file'); }
  const copy = new File([buffer], file.name, { type: file.type, lastModified: file.lastModified });
  const isHeic = /image\/hei[cf]/.test(file.type) || HEIC_PATTERN.test(file.name);
  const resizable = isHeic || /image\/(jpeg|png|webp)/.test(file.type);
  if (!resizable) { return copy; }

  return loadImage(copy).then((img) => {
    if (!isHeic && Math.max(img.naturalWidth, img.naturalHeight) <= MAX_IMAGE_EDGE) { return copy; }
    return toJpeg(img).then((jpeg) => (
      new File([jpeg], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })
    ));
  }, (error) => {
    // Browsers that can't decode HEIC still upload the original copy;
    // the server has Cloudinary convert it
    if (isHeic) { return copy; }
    throw error;
  });
});

const submitQueuedForm = () => {
  if (pending > 0 || !queuedForm) { return; }
  const form = queuedForm;
  queuedForm = null;
  if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
};

const handleChange = (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'file') { return; }
  if (input.hasAttribute('data-manual-files') || typeof DataTransfer === 'undefined') { return; }
  const files = Array.from(input.files);
  if (files.length === 0) { return; }

  pickCount += 1;
  const pick = pickCount;
  input.dataset.pick = pick;
  pending += 1;

  Promise.all(files.map(prepareFile)).then((prepared) => {
    if (input.dataset.pick !== String(pick)) { return; } // a newer pick replaced these
    const transfer = new DataTransfer();
    prepared.forEach((file) => transfer.items.add(file));
    input.files = transfer.files;
  }).catch(() => {
    if (input.dataset.pick !== String(pick)) { return; }
    input.value = '';
    alert('Ein Bild konnte nicht gelesen werden. Bitte wähle es noch einmal aus.');
  }).finally(() => {
    pending -= 1;
    submitQueuedForm();
  });
};

// Hold back submits until the picked files are in memory
const handleSubmit = (event) => {
  if (pending === 0) { return; }
  event.preventDefault();
  event.stopImmediatePropagation();
  queuedForm = event.target;
};

const reliableFileInputs = () => {
  document.addEventListener('change', handleChange, true);
  document.addEventListener('submit', handleSubmit, true);
};

export { reliableFileInputs };
